import {
  clearAuthAndRedirect,
  refetchAccessToken,
  refetchUserPermissions,
} from "../hooks/useCheckTokenAndFetchUser";
import { jwtDecode } from "jwt-decode";
import { z } from "zod";

import { config } from "@/config";
import { PRESIGNED_EXTRACTION_CAPABILITY_HEADER } from "../lib/documentExtraction";
import { metricsRequestHeaders } from "../lib/metrics/requestHeaders";

const apiURL = config.apiUrl;

// The `provider` claim (minted by the backend's finish_login) identifies which
// identity provider owns the current session ("optro" | "workos"). Used to
// force a provider-correct re-login: only an Optro-originated session may be
// bounced to the Optro authorize endpoint.
export const currentSessionProvider = (): string | null => {
  const token = localStorage.getItem("access_token");
  if (!token) return null;
  try {
    return jwtDecode<{ provider?: string }>(token).provider ?? null;
  } catch {
    return null;
  }
};

export class ApiError extends Error {
  status: number;
  detail?: string;
  // `detail` exactly as the backend sent it — FastAPI allows structured
  // payloads (e.g. the 409 workspace-switch instruction), which the string
  // field above can't carry. Callers narrow it with a schema.
  detailData?: unknown;
  // True when the request failed at the network level (fetch rejected before
  // any HTTP response arrived — connection dropped, gateway killed the
  // request, offline, ...). Lets callers distinguish "never got an answer"
  // from a genuine server error.
  isNetworkError = false;

  constructor(status: number, detail?: unknown) {
    // A blank string is treated the same as no detail at all: `detail: ""`
    // is a real shape a backend can send, and without this check it would
    // pass the `typeof === "string"` test, survive every `?? `-based
    // fallback downstream (an empty string isn't null/undefined), and reach
    // the UI as a silently blank error line — indistinguishable from the
    // build succeeding to anyone not reading closely. Every caller of this
    // class gets a real, non-empty message instead. The stored value is the
    // trimmed form too, so it matches the check that admitted it; the raw,
    // untrimmed value is still available on `detailData` for callers that
    // need the backend's string byte for byte.
    const detailText =
      typeof detail === "string" && detail.trim() !== ""
        ? detail.trim()
        : undefined;
    super(detailText || `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detailText;
    this.detailData = detail;
  }

  getUserMessage(): string {
    // Provide user-friendly messages based on status code
    if (this.detail) {
      return this.detail;
    }

    switch (this.status) {
      case 400:
        return "Invalid request. Please check your input.";
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "This action conflicts with existing data.";
      case 500:
        return "Server error. Please try again later.";
      default:
        return "An error occurred. Please try again.";
    }
  }
}

// An Optro view-access refusal (`detail.code` = optro_*). It says what Optro
// decided about one entity, not that the user's Midship role changed.
const OptroRefusalBodySchema = z.object({
  detail: z.object({ code: z.string().startsWith("optro_") }),
});

// Whether a 403 is worth a silent /user/ refetch: not for /user/ itself (it
// would loop), and not for Optro view access — the access check and any
// optro_* refusal — where Midship permissions didn't change and the refetch
// would only delay the error page.
const shouldRefetchPermissionsOn403 = (
  path: string,
  errorBody: unknown,
): boolean =>
  !path.startsWith("/user/") &&
  !path.startsWith("/optro-access/") &&
  !OptroRefusalBodySchema.safeParse(errorBody).success;

interface FetchFastApiParams {
  body?: Record<string, string | number | object | boolean | null> | FormData;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  retryNumber?: number;
  query?:
    | string
    | Record<string, string>
    | URLSearchParams
    | string[][]
    | undefined;
  // Legacy callers read `error_message` off a resolved response; parse-then-
  // render callers opt in so a refusal surfaces as a typed `ApiError` carrying
  // its status instead of parsing as an authoritative empty result.
  throwOnErrorEnvelope?: boolean;
}

export const fetchFastApi = async ({
  body,
  method,
  path,
  query,
  retryNumber = 0,
  throwOnErrorEnvelope = false,
}: FetchFastApiParams) => {
  let url = `${apiURL}${path}`;
  const token = localStorage.getItem("access_token");
  const workspaceId = localStorage.getItem("workspace_id");

  try {
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
      "X-Workspace-Id": workspaceId || "",
      // Opts this bundle in to the presigned `extractionUrlInfo` shape on the
      // document endpoints. Declared for every request rather than at the one
      // call site that reads it, so no future document request can omit it and
      // be served the inline payload the presigned copy exists to avoid.
      [PRESIGNED_EXTRACTION_CAPABILITY_HEADER]: "true",
      // Attributes any event the backend emits for this request to the browser
      // session that caused it (MS-3430). Declared for every request: a path
      // that omits it produces events with no session, silently.
      ...metricsRequestHeaders(),
    };

    let requestBody: string | FormData | null = null;

    if (method !== "GET" && body) {
      if (body instanceof FormData) {
        requestBody = body;
        // Don't set Content-Type for FormData, let the browser set it
      } else {
        headers["Content-Type"] = "application/json";
        requestBody = JSON.stringify(body);
      }
    }
    if (method === "GET" && query) {
      const queryString = new URLSearchParams(query).toString();
      url = url.concat(`?${queryString}`);
    }

    const response = await fetch(url, {
      method,
      headers,
      body: requestBody,
    });

    const contentType = response.headers.get("content-type");

    if (!response.ok) {
      // A Response body is a single-use stream, so it is read ONCE here and
      // reused by every branch below. Reading it again (the 401 branch used to)
      // makes the later parse throw "body stream already read", which the catch
      // swallows — silently discarding the backend's error_message.
      const errorBody = await response.json().catch(() => ({}));

      if (response.status === 401 && path !== "/auth/refresh-tokens") {
        // The backend could not refresh this user's Optro credential
        // (optroReauthRequired). Bounce an Optro-originated session back through
        // the Optro authorize endpoint, preserving where they were headed. Gate
        // on the session's own provider claim so a WorkOS/password session —
        // even one with a linked Optro credential — is never redirected to Optro.
        if (
          errorBody.optroReauthRequired === true &&
          currentSessionProvider() === "optro"
        ) {
          // Re-auth goes through the "Sign in with Optro" domain dialog on the
          // sign-in page (the login endpoint needs the chosen instance URL from
          // the dialog). Clear the client session FIRST — optroReauthRequired
          // means the downstream Optro credential failed, not the Midship JWT, so
          // a still-valid JWT would otherwise let AuthenticatedRedirect bounce the
          // user straight back to /dashboard (reauth loop). clearAuthAndRedirect
          // removes the tokens, stashes the return path, and navigates to /signin.
          clearAuthAndRedirect();
          if (throwOnErrorEnvelope) {
            throw new ApiError(response.status, errorBody.error_message);
          }
          return errorBody;
        }
        if (errorBody.detail?.refetchToken) {
          const refetchResponse = await refetchAccessToken();
          if (refetchResponse && retryNumber < 2) {
            const retriedResponse: string = await fetchFastApi({
              body,
              method,
              path,
              retryNumber: retryNumber + 1,
              throwOnErrorEnvelope,
            });
            return retriedResponse;
          }
        }
      }
      // Reactive-on-403: silently refetch /user/ so the UI's cached permissions
      // catch up with the backend (e.g. after an admin changed our role). We
      // still throw the original error — no auto-retry. Skipped where it
      // can't help; see shouldRefetchPermissionsOn403.
      if (
        response.status === 403 &&
        shouldRefetchPermissionsOn403(path, errorBody)
      ) {
        await refetchUserPermissions();
      }

      // Return the error response so error_message can be accessed
      if (errorBody.error_message) {
        if (throwOnErrorEnvelope) {
          throw new ApiError(response.status, errorBody.error_message);
        }
        return errorBody;
      }

      throw new ApiError(response.status, errorBody?.detail);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else if (
      contentType &&
      (contentType.includes("application/octet-stream") ||
        contentType.includes("application/zip") ||
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ))
    ) {
      return await response.blob();
    } else {
      return await response.text();
    }
  } catch (e) {
    if (e instanceof ApiError) {
      throw e;
    }
    // For unexpected errors, wrap them in ApiError
    console.error("Unexpected error in fetchFastApi:", e);
    const wrapped = new ApiError(500, "An unexpected error occurred");
    // fetch rejects with a TypeError when the request dies at the network
    // level; tag it so callers can treat the outcome as indeterminate.
    wrapped.isNetworkError = e instanceof TypeError;
    throw wrapped;
  }
};
